import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HeartPulse, Loader2, Languages, Building2, UserRound } from "lucide-react";

const CENTER_TYPES = ["RI", "RTF", "RPA"];

export default function Register() {
  const { register } = useAuth();
  const { t, toggleLang, lang } = useT();
  const [f, setF] = useState({
    center_name: "", center_type: "RI", contact_first_name: "", contact_last_name: "",
    address: "", owner_first_name: "", owner_last_name: "", owner_email: "", owner_password: "", confirm: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.owner_password.length < 6) return setError(t("passwordTooShort"));
    if (f.owner_password !== f.confirm) return setError(t("passwordsDontMatch"));
    setLoading(true);
    try {
      const { confirm, ...payload } = f; // eslint-disable-line no-unused-vars
      await register(payload);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-sidebar sidebar-noise text-sidebar-foreground overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="flex items-center gap-3 relative z-10">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center">
            <HeartPulse className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-display text-xl font-extrabold leading-none">{t("appName")}</p>
            <p className="text-sidebar-muted text-xs mt-1">{t("tagline")}</p>
          </div>
        </div>
        <div className="relative z-10 space-y-6">
          <h1 className="font-display text-4xl font-extrabold tracking-tight leading-tight">
            {lang === "fr" ? "Lancez votre résidence en quelques minutes." : "Launch your residence in minutes."}
          </h1>
          <p className="text-sidebar-muted text-lg max-w-md">
            {lang === "fr"
              ? "Créez votre centre et votre compte propriétaire, puis invitez votre équipe."
              : "Create your center and owner account, then invite your team."}
          </p>
          <div className="flex gap-6 pt-4">
            {[t("feat1"), t("feat2"), t("feat3")].map((x) => (
              <div key={x} className="text-sm text-sidebar-muted border-l-2 border-primary pl-3">{x}</div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-sidebar-muted text-sm">© 2026 Residence Connect · Québec</div>
      </div>

      {/* Right form */}
      <div className="flex flex-col items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-md animate-fade-up py-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2 lg:hidden">
              <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
                <HeartPulse className="w-5 h-5 text-white" />
              </div>
              <span className="font-display font-bold text-lg">{t("appName")}</span>
            </div>
            <button onClick={toggleLang} data-testid="register-lang-toggle"
              className="ml-auto flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
              <Languages className="w-4 h-4" /> {lang === "fr" ? "English" : "Français"}
            </button>
          </div>

          <h2 className="font-display text-3xl font-bold tracking-tight">{t("registerTitle")}</h2>
          <p className="text-muted-foreground mt-2 mb-6">{t("registerSubtitle")}</p>

          <form onSubmit={submit} className="space-y-5">
            {/* Center info */}
            <div className="space-y-4 p-4 rounded-xl border border-border bg-card">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />{t("centerInfo")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5 col-span-2"><Label className="text-xs">{t("centerName")}</Label>
                  <Input value={f.center_name} onChange={(e) => set("center_name", e.target.value)} required data-testid="reg-center-name" /></div>
                <div className="space-y-1.5"><Label className="text-xs">{t("centerType")}</Label>
                  <Select value={f.center_type} onValueChange={(v) => set("center_type", v)}>
                    <SelectTrigger data-testid="reg-center-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CENTER_TYPES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
                      <SelectItem value="Autre">{t("centerTypeOther")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">{t("address")}</Label>
                  <Input value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="reg-address" /></div>
                <div className="space-y-1.5"><Label className="text-xs">{t("contactFirstName")}</Label>
                  <Input value={f.contact_first_name} onChange={(e) => set("contact_first_name", e.target.value)} required data-testid="reg-contact-first" /></div>
                <div className="space-y-1.5"><Label className="text-xs">{t("contactLastName")}</Label>
                  <Input value={f.contact_last_name} onChange={(e) => set("contact_last_name", e.target.value)} required data-testid="reg-contact-last" /></div>
              </div>
            </div>

            {/* Owner account */}
            <div className="space-y-4 p-4 rounded-xl border border-border bg-card">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5" />{t("ownerAccount")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label className="text-xs">{t("firstName")}</Label>
                  <Input value={f.owner_first_name} onChange={(e) => set("owner_first_name", e.target.value)} required data-testid="reg-owner-first" /></div>
                <div className="space-y-1.5"><Label className="text-xs">{t("lastName")}</Label>
                  <Input value={f.owner_last_name} onChange={(e) => set("owner_last_name", e.target.value)} required data-testid="reg-owner-last" /></div>
                <div className="space-y-1.5 col-span-2"><Label className="text-xs">{t("email")}</Label>
                  <Input type="email" value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} required data-testid="reg-owner-email" autoComplete="username" /></div>
                <div className="space-y-1.5"><Label className="text-xs">{t("password")}</Label>
                  <Input type="password" value={f.owner_password} onChange={(e) => set("owner_password", e.target.value)} required data-testid="reg-owner-password" autoComplete="new-password" /></div>
                <div className="space-y-1.5"><Label className="text-xs">{t("confirmPassword")}</Label>
                  <Input type="password" value={f.confirm} onChange={(e) => set("confirm", e.target.value)} required data-testid="reg-owner-confirm" autoComplete="new-password" /></div>
              </div>
            </div>

            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" className="w-full h-11" disabled={loading} data-testid="register-submit-button">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />{t("registering")}</> : t("signUp")}
            </Button>
          </form>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            {t("haveAccount")} <Link to="/" className="text-primary font-medium hover:underline" data-testid="go-to-login">{t("signIn")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
